import React, { useState } from "react"

import { styled } from "@mui/material/styles"

import { useTranslations } from "@cirrus/core/i18n/provider"
import { getDateRelative } from "@cirrus/common/helpers/getDateRelative"
import { ActionIcon, Avatar, EditableString, MuiIcon, Tooltip } from "@cirrus/ui-kit-ts"

import { LikeCounter } from "./LikeCounter"
import { UserTooltip } from "./UserTooltip"
import { useConfirmDialogEventBus } from "../../../ConfirmDialog/useConfirmDialogEventBus"
import { useDateFormat } from "../../../hooks/useDateFormat"
import { Name } from "../styled/Name"
import { IComment } from "../types/IComment"
import { IPostComment } from "../types/IPostComment"

interface IProps<T extends IComment<T, TMention, TRole>, TMention, TRole> extends IComment<T, TMention, TRole> {
  isReply?: boolean
  toggleLike: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (comment: IPostComment) => void
  toggleReply: () => void
  revision?: number
  getCommentRole?: (comment) => string
}

export const MessageItem = <T extends IComment<T, TMention, TRole>, TMention, TRole>({
                                                                                       isReply = false,
                                                                                       toggleReply,
                                                                                       onEdit,
                                                                                       getCommentRole,
                                                                                       ...item
                                                                                     }: IProps<T, TMention, TRole>) => {
  const [editMode, setEditMode] = useState<boolean>(false)
  const [currentValue, setCurrentValue] = useState<string>(item.text)
  const bus = useConfirmDialogEventBus()

  const { i18n } = useTranslations()
  const { formatDateUserFriendly } = useDateFormat()

  const onEditHandler = (text: string) => {
    onEdit({ id: item.id, text })
    setEditMode(false)
  }

  const onDeleteHandler = () => {
    bus.emit("ConfirmDialog > Open", {
      title: i18n("General:Confirm"),
      text: i18n("General:ConfirmDeleteComment"),
      confirmBtnColor: "error",
      confirm: () => item.onDelete(item.id),
      width: "31rem"
    })
  }

  if (item.isDeleted) {
    return <Wrapper className={`${isReply && "reply"}`}>
      {i18n("General:CommentWasDeleted")}
    </Wrapper>
  }

  const userFriendlyDate = formatDateUserFriendly(item.date)
  const name = item.name
    || (item.isDeleted ? i18n("Users:DeletedUser") : i18n("General:Anonymous"))

  return <Wrapper className={`${isReply && "reply"} ${editMode && "edit-mode"}`}>
    <UserTooltip name={name}
                 avatarUrl={item.avatarUrl}
                 role={getCommentRole?.(item) ?? ""}
                 isOnline={item.isOnline}
                 lastSeen={item.lastSeen}>

      <Avatar title={name}
              picUrl={item.avatarUrl}
              tooltipDisabled />
    </UserTooltip>

    <Content>
      <Header>
        <div>
          <Name>{name}</Name>
          <Date>
            <Tooltip title={userFriendlyDate.dtFull}>
              {getDateRelative(item.date)}
            </Tooltip>
          </Date>
          {item?.revision && (
            <RevisionNumber>{i18n("General:Revision")} {item?.revision}</RevisionNumber>
          )}
        </div>
        {
          item.canEdit
          &&
          <ActionsWrapper>
            {
              editMode
                ? <ActionIcon icon={MuiIcon.SaveOutlined} onClick={() => onEditHandler(currentValue)} />
                : <ActionIcon icon={MuiIcon.ModeEdit} onClick={() => setEditMode(!editMode)} />
            }
            <ActionIcon icon={MuiIcon.DeleteOutlined}
                        color="error"
                        onClick={onDeleteHandler} />
          </ActionsWrapper>
        }
      </Header>

      <EditableString
        value={item.text}
        isEditMode={editMode}
        onApply={onEditHandler}
        onChange={setCurrentValue}
        multiline />

      <ActionsWrapper>
        <LikeCounter {...item} toggleLike={item.toggleLike} />
        <ActionIcon icon={MuiIcon.ReplyOutlined} onClick={toggleReply} />
      </ActionsWrapper>
    </Content>
  </Wrapper>
}

const Wrapper = styled("div")({
  display: "flex",
  gap: ".8rem",
  width: "100%",
  color: "#1A1A1A",
  padding: "1rem .8rem",
  background: "#F5F7FA",
  fontSize: "1.4rem",
  borderRadius: ".4rem",

  "&.reply": {
    background: "#F7F7F7",
    marginLeft: "2.8rem",
    width: "calc(100% - 2.8rem)"
  },

  "&.edit-mode": {
    background: "#fff",
    border: ".1rem solid"
  }
})

const Header = styled("div")({
  display: "flex",
  lineHeight: "2rem",
  justifyContent: "space-between"
})

const Date = styled("div")({
  fontSize: "1.2rem"
})

const ActionsWrapper = styled("div")({
  display: "flex",
  gap: ".8rem"
})

const Content = styled("div")({
  display: "flex",
  flexDirection: "column",
  gap: ".8rem",
  marginRight: "1.6rem",
  width: "100%"
})

export const RevisionNumber = styled("div")({
  fontSize: "12px",
  fontWeight: 400,
  fontStyle: "italic",
  color: "#737373",
  lineHeight: "15px",
  marginBottom: "1px",
})
